// src/pages/AgenticAISimulator.jsx
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  agenticAIIntro,
  agenticAIPromptData,
} from "./AgenticAIGateway";


export default function AgenticAISimulator() {
  // const [input, setInput] = useState("");
  const location = useLocation();
  const passedPrompt = location.state?.prompt || "";
  const [input, setInput] = useState(passedPrompt); 

  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: "🌍 Namaste! I am Think5Whys Agent. How may I assist you today?",
    },
  ]);
  const [isThinking, setIsThinking] = useState(false);
  const [plan, setPlan] = useState(null);
  const [showPlan, setShowPlan] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg = { role: "user", text: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsThinking(true);
    setPlan(null);

    const { steps, plan } = await simulateAgentResponse(input);
    setPlan(plan);
    setMessages((prev) => [...prev, ...steps]);
    setIsThinking(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-teal-950 to-emerald-950 text-gray-100 flex flex-col">
      {/* Header */}
      <header className="text-center py-2 bg-teal-900/70 backdrop-blur-md flex gap-1 justify-between px-4 items-center border-b border-teal-800">
        <div className="flex flex-col place-items-start justify-start">
          <p className="text-lg lg:text-xl font-semibold text-yellow-200">
            🤖 Thinky: AI Orchestration
          </p>
          <p className="text-xs lg:text-sm text-white text-left">
            Individuals, MSME, Families, Workers, Professionals, Farmers, Women, Governemnt, Corporate, Youth....
          </p>
        </div>
        
        <div className="mt-2 flex flex-col w-28 place-items-end">
          {plan && (
            <button
              onClick={() => setShowPlan(!showPlan)}
              className="text-xs lg:text-sm bg-blue-600 px-1 py-1 w-full rounded-lg hover:bg-blue-500 transition"
            >
              {showPlan ? "Hide Plan" : "View Plan"}
            </button>
          )}
        </div>
      </header>

      {/* Agent Plan View */}
      {showPlan && plan && (
        <div className="mx-4 my-2 bg-teal-800/70 p-3 rounded-xl border border-emerald-600 shadow-inner text-sm">
          <h2 className="font-semibold text-emerald-300 mb-2">
            🧠 Agent Plan Summary
          </h2>
          <p>
            <strong>Intent:</strong> {plan.intent}
          </p>
          <ul className="mt-2 list-disc list-inside space-y-1">
            {plan.actions.map((a, i) => (
              <li key={i}>
                <span className="text-emerald-400 font-medium">
                  {a.toolName}
                </span>{" "}
                → <span className="italic">{a.description}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3">
            <strong>Expected Outcome:</strong> {plan.outcome}
          </p>
        </div>
      )}

      {/* Chat Section */}
      <main className="flex-1 flex flex-col p-4 overflow-y-auto space-y-3">
        {messages.map((msg, idx) => (
          <MessageBubble key={idx} role={msg.role} text={msg.text} />
        ))}
        {isThinking && <ThinkingBubble />}
      </main>

      {/* Input Footer */}
      <footer className="p-3 bg-teal-900 border-t border-teal-700 flex gap-2">
        <input
          className="flex-1 p-2 h-12 rounded-l-xl bg-black text-white placeholder-gray-400 focus:outline-none"
          placeholder="Ask something like — find jobs abroad, teacher training, student scholarship..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
      <div className="flex space-x-2">
        <button
          onClick={handleSend}
          className="bg-transparent text-sm lg:text-3xl h-10 w-10 flex items-center justify-center text-white rounded-full 
                    hover:bg-blue-500 hover:scale-110 transition transform duration-300 ease-in-out active:scale-95"
          aria-label="Send"
        >
          ⬆️
        </button>

        <button
          onClick={() => setShowHelp(true)}
          className="bg-transparent text-sm lg:text-3xl h-10 w-10 flex items-center justify-center text-white rounded-full 
                    hover:bg-blue-500 hover:scale-110 transition transform duration-300 ease-in-out active:scale-95"
          aria-label="Help"
        >
          ❓
        </button>
      </div>

      </footer>

      {showHelp && <HelpModal setInput={setInput} onClose={() => setShowHelp(false)} />}
    </div>
  );
}

function MessageBubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${
        isUser ? "justify-end" : "justify-start"
      } animate-fadeIn`}
    >
      <div
        className={`max-w-[80%] p-3 rounded-2xl ${
          isUser
            ? "bg-emerald-600 text-white rounded-br-none"
            : "bg-teal-800 text-emerald-200 rounded-bl-none"
        } shadow-md`}
      >
        {text}
      </div>
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="flex justify-start space-x-1 p-3 animate-pulse">
      <div className="w-2 h-2 bg-emerald-300 rounded-full"></div>
      <div className="w-2 h-2 bg-emerald-300 rounded-full"></div>
      <div className="w-2 h-2 bg-emerald-300 rounded-full"></div>
    </div>
  );
}

function HelpModal({ setInput, onClose, onCopyPrompt }) {
  const [open, setOpen] = useState(null);
  const [subOpen, setSubOpen] = useState({});
  const [copiedText, setCopiedText] = useState(null); // store copied text
  const toggle = (idx) => setOpen(open === idx ? null : idx);
  const toggleSub = (group, idx) =>
    setSubOpen((p) => ({ ...p, [group]: p[group] === idx ? null : idx }));

  const handleCopy = (text) => {
  if (!text) return;

  // Modern API
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard
      .writeText(text)
      .then(() => setCopiedText(text))
      .catch(() => fallbackCopyTextToClipboard(text));
  } else {
    // Fallback for older browsers / mobiles
    fallbackCopyTextToClipboard(text);
  }

  if (onCopyPrompt) onCopyPrompt(text);

  setTimeout(() => setCopiedText(null), 2000); // toast disappears after 2s
};

// Fallback method using hidden textarea
function fallbackCopyTextToClipboard(text) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed"; // avoid scrolling
  textArea.style.top = "-9999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand("copy");
    setCopiedText(text);
  } catch (err) {
    console.error("Fallback: Copy failed", err);
  }

  document.body.removeChild(textArea);
}


  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50">
      <div className="bg-black border border-emerald-600 rounded-xl p-4 w-full text-white overflow-y-auto max-h-[85vh] shadow-xl relative">
        <h2 className="text-emerald-300 text-lg font-semibold mb-3">🆘 Help — What Can You Ask?</h2>
        
        {/* Main Accordion */}
        <div className="space-y-3">
          {agenticAIPromptData.map((cat, idx) => (
            <Accordion
              key={idx}
              idx={idx}
              open={open}
              onToggle={toggle}
              title={`📌 ${cat.category}`}
              color="gray"
            >
              {cat.subcategories.map((sub, sIdx) => (
                <SubAccordion
                  key={sIdx}
                  group={`${idx}`}
                  idx={sIdx}
                  subOpen={subOpen}
                  toggleSub={toggleSub}
                  title={sub.title}
                  prompts={sub.prompts}
                  color="gray"
                  handleCopy={handleCopy}
                  onSelectPrompt={setInput}
                />
              ))}
            </Accordion>
          ))}
        </div>
        <div className="mt-5 text-center">
          <button
            onClick={onClose}
            className="mb-2 px-2 py-2 w-20 text-sm lg:text-lg bg-black text-white rounded-lg font-semibold hover:bg-emerald-700 transition"
          >
             ❌
          </button>
        </div>
        <div className="bg-black border border-emerald-700 rounded-lg p-3 mb-4 text-sm">
          {agenticAIIntro.description.map((line, i) => (
            <p key={i} className="text-emerald-200 mb-2 last:mb-0">
              {line}
            </p>
          ))}
        </div>
        {copiedText && (
          <div className="absolute bottom-5 right-5 bg-emerald-400 text-black px-3 py-1 rounded shadow-lg animate-fadeInOut">
            ✅ Copied!
          </div>
        )}
      </div>
    </div>
  );
}

function Accordion({ idx, open, onToggle, title, color, children }) {
  const isOpen = open === idx;
  return (
    <div className={`border border-${color}-600 rounded-lg`}>
      <button
        onClick={() => onToggle(idx)}
        className={`w-full p-3 flex justify-between items-center text-left text-${color}-300 font-semibold`}
      >
        <span>{title}</span>
        <span className="text-xl">{isOpen ? "−" : "+"}</span>
      </button>
      {isOpen && <div className={`p-3 bg-black border-t border-${color}-700`}>{children}</div>}
    </div>
  );
}

function SubAccordion({ group, idx, subOpen, toggleSub, title, prompts, color, handleCopy, onSelectPrompt }) {
  const isOpen = subOpen[group] === idx;
  return (
    <div className={`border border-${color}-700 rounded-lg mb-2`}>
      <button
        onClick={() => toggleSub(group, idx)}
        className={`w-full p-2 flex justify-between items-center text-left text-${color}-300 font-medium`}
      >
        <span>{title}</span>
        <span>{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen && (
        <div className="p-3 bg-black border-t border-emerald-700 space-y-2">
          {prompts.map((p) => (
            <div
              key={p.id || p.text}
              className="flex justify-between items-start bg-black border border-emerald-600 rounded-lg p-2"
            >
              <p className="text-white text-sm">{p.text}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation(); 
                  handleCopy(p.text);
                }}
                className="ml-2 px-2 py-1 w-20 text-xs bg-black text-white rounded hover:bg-emerald-500 transition"
              >
                📋 Copy
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export async function simulateAgentResponse(userPrompt) {
  const wait = (ms) => new Promise((res) => setTimeout(res, ms));
  const lowerPrompt = userPrompt.toLowerCase().trim();

  // Flatten all prompts safely
  const allPrompts = agenticAIPromptData.flatMap((cat) =>
    cat.subcategories.flatMap((sub) =>
      sub.prompts.map((p) => ({
        ...p,
        categoryTitle: sub.title,
        parentCategory: cat.category
      }))
    )
  );

  // Keyword-based matching: split user prompt into words
  const inputKeywords = lowerPrompt.split(/\s+/);

  // Rank prompts by number of keyword matches
  const scoredPrompts = allPrompts.map((p) => {
    const promptText = p.text.toLowerCase();
    const matches = inputKeywords.filter((kw) => promptText.includes(kw));
    return { prompt: p, score: matches.length };
  });

  // Find the best matching prompt
  const bestMatch = scoredPrompts
    .filter((p) => p.score > 0)
    .sort((a, b) => b.score - a.score)[0];

  let plan = { intent: "", actions: [], outcome: "", steps: [] };

  if (bestMatch) {
    const matchedPrompt = bestMatch.prompt;
    plan.intent = matchedPrompt.text;
    plan.actions = matchedPrompt.actions.map((a) => ({
      toolName: a,
      description: a
    }));
    plan.outcome = matchedPrompt.outcome || "Action completed successfully.";
    plan.steps = [
      "🤔 Understanding your request...",
      "🧩 Planning next actions...",
      `🔍 Detected intent: ${plan.intent}`,
      ...plan.actions.map((a) => `⚙️ Executing ${a.toolName} → ${a.description}`),
      `✅ ${plan.outcome}`
    ];
  } else {
    // Fallback plan with same style
    plan.intent = "General help or information";
    plan.actions.push({
      toolName: "Think5Whys Core 🤝",
      description: "Handle generic queries and assistance"
    });
    plan.outcome = "Action completed successfully.";
    plan.steps = [
      "🤔 Understanding your request...",
      "🧩 Planning next actions...",
      `🔍 Detected intent: ${plan.intent}`,
      ...plan.actions.map((a) => `⚙️ Executing ${a.toolName} → ${a.description}`),
      `✅ ${plan.outcome}`
    ];
  }

  // Simulate step-by-step reasoning
  const output = [];
  for (const s of plan.steps) {
    await wait(700 + Math.random() * 500);
    output.push({ role: "ai", text: s });
  }

  return { steps: output, plan };
}



